import React, { useEffect, useState } from "react";
import { Activity, BarChart3, Bone, ClipboardList } from "lucide-react";
import html2pdf from "html2pdf.js";
import { useLanguage } from "../contexts/LanguageContext";
import {
  buildClinicalRegistryStats,
  type ClinicalRegistryStats,
} from "../services/clinicalRegistryStats";
import { usePatient } from "../contexts/PatientContext";
import { PremiumActionButton, PremiumEmptyState, PremiumGrid, PremiumInput, PremiumModuleCard, PremiumPageShell, PremiumSectionCard } from './premium';

const ClinicalRegistryModule: React.FC = () => {
  const { t } = useLanguage();
  const { patients, loadingPatients } = usePatient();
  const [stats, setStats] = useState<ClinicalRegistryStats | null>(null);
  const [generatedReport, setGeneratedReport] = useState("");

  useEffect(() => {
    console.log("PACIENTES:", patients);
    setStats(buildClinicalRegistryStats(patients));
  }, [patients]);

  const loading = loadingPatients;

  const renderRankList = (items: { label: string; count: number }[], emptyText: string) => {
    if (!items.length) {
      return <p className="text-sm text-slate-500">{emptyText}</p>;
    }

    return (
      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-800/40 px-4 py-3"
          >
            <span className="text-sm font-medium text-white">{item.label}</span>
            <span className="text-xs font-bold text-slate-400">
              {t("casesCount", { count: item.count })}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleGenerateReport = () => {
    if (!stats) {
      setGeneratedReport("");
      return;
    }

    const formatRankList = (items: { label: string; count: number }[]) =>
      items.length
        ? items.map((item) => `- ${item.label}: ${t("casesCount", { count: item.count })}`).join("\n")
        : `- ${t("insufficientData")}`;

    setGeneratedReport(
      [
        t("reportTitle"),
        "",
        t("totalCasesAnalyzed", { count: stats.totalCases }),
        `${t("averageUnionRate")}: ${
          stats.unionRate !== null && stats.unionRate !== undefined ? `${stats.unionRate}%` : "--"
        }`,
        `${t("averageComplicationRate")}: ${
          stats.complicationRate !== null && stats.complicationRate !== undefined
            ? `${stats.complicationRate}%`
            : "--"
        }`,
        "",
        t("treatmentStrategiesUsed"),
        formatRankList(stats.topTreatmentStrategies),
        "",
        t("mostTreatedBones"),
        formatRankList(stats.topBones),
      ].join("\n")
    );
  };

  const handleExportPdf = () => {
    if (!generatedReport) return;

    const reportElement = document.createElement("div");
    reportElement.style.padding = "32px";
    reportElement.style.background = "#ffffff";
    reportElement.style.color = "#0f172a";
    reportElement.style.fontFamily = "Arial, sans-serif";
    reportElement.style.lineHeight = "1.6";
    reportElement.style.whiteSpace = "pre-wrap";
    reportElement.textContent = generatedReport;

    document.body.appendChild(reportElement);

    const options = {
      margin: 0.5,
      filename: "relatorio_registro_clinico_orthoprecision.pdf",
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" as const },
    };

    void html2pdf()
      .from(reportElement)
      .set(options)
      .save()
      .finally(() => {
        if (reportElement.parentNode) {
          reportElement.parentNode.removeChild(reportElement);
        }
      });
  };

  return (
    <PremiumPageShell className="space-y-6 animate-in fade-in duration-500 pb-12">
      <PremiumSectionCard
        title={t("clinicalRegistryTitle")}
        subtitle={t("clinicalRegistrySubtitle")}
        className="p-6"
        actions={
          <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={handleGenerateReport}
            disabled={loading || !stats}
            className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("generateScientificReport")}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={!generatedReport}
            className="rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-4 py-2 text-sm font-semibold text-[color:var(--op-text)] transition-colors hover:bg-cyan-500/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("exportReportPdf")}
          </button>
          </div>
        }
      />

      {loading ? (
        <div className="glass rounded-2xl border border-[color:var(--op-border)] p-8 text-sm text-[color:var(--op-text-muted)]">
          {t("loading")}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="glass space-y-2 rounded-2xl border border-[color:var(--op-border)] p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)]">
                {t("totalRegisteredCases")}
              </p>
              <p className="text-3xl font-bold text-[color:var(--op-text)]">{stats?.totalCases ?? 0}</p>
            </div>

            <div className="glass space-y-2 rounded-2xl border border-[color:var(--op-border)] p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)]">
                {t("averageUnionRate")}
              </p>
              <p className="text-3xl font-bold text-[color:var(--op-text)]">
                {stats?.unionRate !== null && stats?.unionRate !== undefined
                  ? `${stats.unionRate}%`
                  : "--"}
              </p>
            </div>

            <div className="glass space-y-2 rounded-2xl border border-[color:var(--op-border)] p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)]">
                {t("averageComplicationRate")}
              </p>
              <p className="text-3xl font-bold text-[color:var(--op-text)]">
                {stats?.complicationRate !== null && stats?.complicationRate !== undefined
                  ? `${stats.complicationRate}%`
                  : "--"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="glass space-y-4 rounded-2xl border border-[color:var(--op-border)] p-6">
              <h3 className="flex items-center gap-2 text-lg font-bold text-[color:var(--op-text)]">
                <ClipboardList className="h-5 w-5 text-emerald-300" />
                {t("treatmentStrategiesUsed")}
              </h3>
              {renderRankList(
                stats?.topTreatmentStrategies || [],
                t("noStrategiesInRegistry")
              )}
            </div>

            <div className="glass space-y-4 rounded-2xl border border-[color:var(--op-border)] p-6">
              <h3 className="flex items-center gap-2 text-lg font-bold text-[color:var(--op-text)]">
                <Bone className="h-5 w-5 text-cyan-300" />
                {t("mostTreatedBones")}
              </h3>
              {renderRankList(
                stats?.topBones || [],
                t("noBonesInRegistry")
              )}
            </div>
          </div>

          {generatedReport && (
            <div className="glass space-y-4 rounded-2xl border border-[color:var(--op-border)] p-6">
              <h3 className="flex items-center gap-2 text-lg font-bold text-[color:var(--op-text)]">
                <Activity className="h-5 w-5 text-cyan-300" />
                {t("scientificReport")}
              </h3>
              <textarea
                readOnly
                value={generatedReport}
                className="min-h-64 w-full resize-none rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-4 text-sm text-[color:var(--op-text)] outline-none"
              />
            </div>
          )}
        </div>
      )}
    </PremiumPageShell>
  );
};

export default ClinicalRegistryModule;
